export default function TechnologyApproach() {
  const technologies = [
    {
      icon: 'ri-bubble-chart-line',
      title: 'Enhanced Anaerobic Digestion',
      description:
        'Converts organic material in wastewater into methane-rich biogas through a biological process where microorganisms break down waste in the absence of oxygen. The captured biogas is then used to generate renewable electricity and heat.',
      highlights: [
        'Methane-rich biogas capture',
        'Renewable electricity generation',
        'Heat recovery systems',
        'Biological waste breakdown',
      ],
      badge: 'Biological Process',
      color: 'emerald',
    },
    {
      icon: 'ri-fire-line',
      title: 'Thermochemical Sludge Conversion',
      description:
        'For sludge streams that cannot be efficiently processed through biological digestion, thermochemical technologies convert sewage sludge into energy-rich gases while enabling the recovery of valuable nutrients.',
      highlights: [
        'Energy-rich gas production',
        'Nutrient recovery',
        'Sludge stream processing',
        'High-temperature conversion',
      ],
      badge: 'Thermochemical Process',
      color: 'amber',
    },
  ];

  return (
    <section id="technology" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            Technology Approach
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Advanced <strong>Wastewater-to-Value</strong> Technologies
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Kagesi deploys complementary technologies that combine enhanced anaerobic digestion and thermochemical sludge conversion to maximise energy and resource recovery from wastewater systems.
          </p>
        </div>

        {/* Two Technology Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {technologies.map((tech, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl border border-gray-200 p-10 flex flex-col gap-6 shadow-sm hover:shadow-md transition-shadow duration-300"
            >
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 bg-${tech.color}-100 rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <i className={`${tech.icon} text-${tech.color}-600 text-2xl`}></i>
                </div>
                <div>
                  <span className={`text-xs font-semibold text-${tech.color}-600 bg-${tech.color}-50 px-3 py-1 rounded-full`}>
                    {tech.badge}
                  </span>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{tech.title}</h3>
                <p className="text-gray-600 leading-relaxed">{tech.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2">
                {tech.highlights.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <i className={`ri-checkbox-circle-fill text-${tech.color}-500 text-lg flex-shrink-0 mt-0.5`}></i>
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Integration Banner */}
        <div className="relative bg-gradient-to-br from-emerald-800 to-emerald-600 rounded-2xl overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://public.readdy.ai/ai/img_res/d541ec24626420a8955bb513a76c9c5f.jpg"
              alt="Integration background"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="relative z-10 px-10 py-12 lg:px-16 lg:py-14">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold mb-5">
                  <i className="ri-links-line text-base"></i>
                  Integrated System
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                  Maximising Recovery Through Integration
                </h3>
                <p className="text-emerald-100 leading-relaxed text-base max-w-2xl">
                  By integrating these complementary processes, Kagesi maximises resource recovery from wastewater systems while reducing sludge disposal costs, lowering methane emissions, and transforming wastewater treatment infrastructure into clean energy–producing circular systems.
                </p>
              </div>
              <div className="flex flex-col gap-4 lg:min-w-[260px]">
                {[
                  { icon: 'ri-recycle-line', label: 'Maximum Resource Recovery' },
                  { icon: 'ri-money-dollar-circle-line', label: 'Reduced Sludge Disposal Costs' },
                  { icon: 'ri-leaf-line', label: 'Lower Methane Emissions' },
                  { icon: 'ri-building-2-line', label: 'Circular Infrastructure Assets' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-white/10 rounded-xl px-5 py-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-emerald-200 text-xl`}></i>
                    </div>
                    <span className="text-white text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
